<script>
import { mapActions, mapGetters } from 'vuex'
import ValidationMixin from '@lib/mixins/validation'

export default {
  name: 'ProviderForm',
  mixins: [ValidationMixin],
  props: {
    provider: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {
        name: null,
      },
      rules: {
        name: [
          {
            required: true,
            message: 'This field is required',
          },
        ],
      },
      backRoute: { name: 'providers' },
      progress: false,
      showDeleteDialog: false,
    }
  },
  computed: {
    ...mapGetters('me', [
      'canSearchProvider',
      'canCreateProvider',
      'canUpdateProvider',
      'canDeleteProvider',
    ]),
    isUpdating() {
      return this.provider !== null
    },
    isFormChanged() {
      return this.provider.name !== this.form.name
    },
    isSaveBtnDisabled() {
      return (this.isUpdating && !this.isFormChanged) || this.progress
    },
  },
  mounted() {
    if (this.isUpdating) {
      this.form = { ...this.provider }
    }
  },
  methods: {
    ...mapActions('providers', {
      createProvider: 'POST_PROVIDER',
      updateProvider: 'PUT_PROVIDER',
      deleteProvider: 'DELETE_PROVIDER',
    }),
    goBack() {
      this.$router.replace(this.backRoute)
    },
    onCancel() {
      this.$router.back()
    },
    async onSubmit() {
      this.clearBackendErrors()
      if (this.isSaveBtnDisabled) return

      const formValid = await this.$refs.form.validate().catch(() => false)
      if (!formValid) return

      this.progress = true

      const response = await this[this.isUpdating ? 'updateProvider' : 'createProvider'](this.form)
      const [error] = response

      if (error) {
        this.processBackendErrors(error)
      }

      this.$notifier({
        response,
        errorTitle: `Unable to ${this.isUpdating ? 'update' : 'create'} provider`,
        successTitle: 'Provider created',
        successMsg: `Provider successfully ${this.isUpdating ? 'updated' : 'created'}`,
      }).then(success => {
        if (success) {
          this.goBack()
        }
      })

      this.progress = false
    },
    async onDelete() {
      this.showDeleteDialog = false
      this.progress = true

      await this.deleteProvider(this.form.id).then(response => {
        this.$notifier({
          response,
          errorTitle: 'Unable to delete provider',
          successTitle: 'Provider deleted',
          successMsg: 'Provider successfully deleted',
        }).then(success => {
          if (success) {
            this.goBack()
          }
        })
      })

      this.progress = false
    },
  },
}
</script>

<template>
  <div>
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-position="top"
      class="card-form"
    >
      <el-form-item
        label="Provider Name"
        prop="name"
        :error="backendErrors.name"
      >
        <el-input v-model="form.name" />
      </el-form-item>

      <div class="card-form__footer">
        <div v-if="isUpdating && canDeleteProvider">
          <el-button
            type="danger"
            :disabled="progress"
            @click.stop="showDeleteDialog = true"
          >
            Delete Provider
          </el-button>
        </div>
        <div>
          <el-button
            v-if="canSearchProvider"
            :disabled="progress"
            @click="onCancel"
          >
            Cancel
          </el-button>
          <el-button
            v-if="isUpdating ? canUpdateProvider : canCreateProvider"
            type="primary"
            :disabled="isSaveBtnDisabled"
            @click="onSubmit"
          >
            Save Provider
          </el-button>
        </div>
      </div>
    </el-form>

    <state-dialog
      :visible.sync="showDeleteDialog"
      title="Delete Provider"
      brand="danger"
    >
      <el-icon
        slot="icon"
        name="warning"
      />
      <el-button
        slot="subicon"
        icon="el-icon-delete"
        type="danger"
        circle
      />
      <div :class="$style.msg">
        Are you sure you want to delete this provider?
      </div>
      <div class="modal__footer">
        <el-button
          type="danger"
          class="el-button--wide"
          @click="onDelete"
        >
          Delete Provider
        </el-button>
      </div>
    </state-dialog>
  </div>
</template>

<style lang="scss" module>
  .msg {
    text-align: center;
    white-space: nowrap;
  }

  .subicon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 16px;
    color: var(--color-primary-text);
    background-color: var(--color-error);
    border-radius: 50%
  }
</style>
